import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  useDeleteShapeSize,
  useMaterials,
  useShapeSizes,
  useUpsertShapeSize,
} from "@/features/materials/api";
import { SHAPE_KINDS, labelFor } from "@/features/materials/constants";
import {
  emptyShapeSizeForm,
  shapeSizeFormSchema,
  type ShapeSizeFormValues,
} from "@/features/materials/schema";
import type { ShapeSizeListRow } from "@/features/materials/types";

export function ShapeSizesPanel() {
  const { data: materials = [] } = useMaterials();
  const [materialFilter, setMaterialFilter] = useState<string>("all");
  const { data = [], isLoading, error } = useShapeSizes(
    materialFilter === "all" ? undefined : materialFilter,
  );
  const upsert = useUpsertShapeSize();
  const remove = useDeleteShapeSize();
  const [editing, setEditing] = useState<ShapeSizeListRow | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<ShapeSizeListRow | null>(null);

  const tubeId = useMemo(
    () => materials.find((m) => m.name === "Tube")?.id ?? "",
    [materials],
  );

  const form = useForm<ShapeSizeFormValues>({
    resolver: zodResolver(shapeSizeFormSchema),
    defaultValues: emptyShapeSizeForm,
  });

  function openCreate() {
    setEditing(null);
    form.reset({
      ...emptyShapeSizeForm,
      applicable_material_id: materialFilter !== "all" ? materialFilter : tubeId,
    });
    setOpen(true);
  }

  function openEdit(row: ShapeSizeListRow) {
    setEditing(row);
    form.reset({
      shape: row.shape,
      size_label: row.size_label,
      od: row.od,
      thickness: row.thickness,
      width: row.width,
      length: row.length,
      applicable_material_id: row.applicable_material_id,
      mother_size_id: row.mother_size_id ?? "",
      description: row.description ?? "",
      is_active: row.is_active,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs text-muted-foreground">
            Shape & size master — set a round mother size when this size is formed from it
          </p>
          <NativeSelect
            value={materialFilter}
            onChange={(e) => setMaterialFilter(e.target.value)}
            className="w-40"
            aria-label="Filter by material"
          >
            <option value="all">All materials</option>
            {materials.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <Button type="button" size="sm" onClick={openCreate}>
          <Plus />
          Add size
        </Button>
      </div>

      {error ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="overflow-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Material</th>
              <th className="px-3 py-2 font-semibold">Shape</th>
              <th className="px-3 py-2 font-semibold">Size</th>
              <th className="px-3 py-2 font-semibold">Mother round</th>
              <th className="px-3 py-2 font-semibold">Mother OD</th>
              <th className="px-3 py-2 font-semibold">Thk</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  No shape / sizes
                </td>
              </tr>
            ) : (
              data.map((row) => {
                const mother =
                  row.mother ??
                  (row.mother_size_id
                    ? (data.find((r) => r.id === row.mother_size_id) ?? null)
                    : null);
                return (
                <tr key={row.id} className="border-b border-border/70">
                  <td className="px-3 py-1.5 text-xs">{row.material?.name ?? "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{labelFor(SHAPE_KINDS, row.shape)}</td>
                  <td className="px-3 py-1.5 font-medium">{row.size_label}</td>
                  <td className="px-3 py-1.5 text-2xs text-muted-foreground">
                    {mother?.size_label?.trim()
                      ? `${labelFor(SHAPE_KINDS, mother.shape)} ${mother.size_label}`
                      : "—"}
                  </td>
                  <td className="px-3 py-1.5 tabular text-xs text-muted-foreground">
                    {row.od ?? "—"}
                  </td>
                  <td className="px-3 py-1.5 tabular text-xs text-muted-foreground">
                    {row.thickness ?? "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    <StatusDot tone={row.is_active ? "success" : "neutral"}>
                      {row.is_active ? "Active" : "Inactive"}
                    </StatusDot>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Edit ${row.size_label}`}
                      onClick={() => openEdit(row)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Delete ${row.size_label}`}
                      className="hover:text-destructive"
                      onClick={() => setDeleting(row)}
                    >
                      <Trash2 />
                    </Button>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit shape / size" : "New shape / size"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field
                label="Material"
                required
                span={6}
                error={form.formState.errors.applicable_material_id?.message}
                htmlFor="ss-material"
              >
                <NativeSelect id="ss-material" {...form.register("applicable_material_id")}>
                  <option value="">Select…</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Shape" span={6} htmlFor="ss-shape">
                <NativeSelect id="ss-shape" {...form.register("shape")}>
                  {SHAPE_KINDS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="Size label"
                required
                span={6}
                error={form.formState.errors.size_label?.message}
                htmlFor="ss-label"
              >
                <Input id="ss-label" placeholder="25.4x1.6" {...form.register("size_label")} />
              </Field>
              <ToggleField
                label="Active"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={6}
              />
              <Field
                label="Mother OD (mm)"
                span={3}
                htmlFor="ss-od"
                hint="Round mother tube OD this size is formed from"
              >
                <Input id="ss-od" type="number" step="0.01" {...form.register("od")} />
              </Field>
              <Field label="Thickness" span={3} htmlFor="ss-thk">
                <Input id="ss-thk" type="number" step="0.01" {...form.register("thickness")} />
              </Field>
              <Field label="Width" span={3} htmlFor="ss-w">
                <Input id="ss-w" type="number" step="0.01" {...form.register("width")} />
              </Field>
              <Field label="Length" span={3} htmlFor="ss-l">
                <Input id="ss-l" type="number" step="0.01" {...form.register("length")} />
              </Field>
              <Field
                label="Mother size"
                span={12}
                htmlFor="ss-mother"
                hint="Round mother this size is formed from (planning / further calcs)"
              >
                <NativeSelect id="ss-mother" {...form.register("mother_size_id")}>
                  <option value="">— None —</option>
                  {data
                    .filter(
                      (r) =>
                        r.id !== editing?.id &&
                        r.applicable_material_id === form.watch("applicable_material_id") &&
                        r.shape === "round",
                    )
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.size_label}
                        {r.od != null ? ` (Ø${r.od}` : ""}
                        {r.thickness != null ? `×${r.thickness}` : ""}
                        {r.od != null ? ")" : ""}
                      </option>
                    ))}
                </NativeSelect>
              </Field>
              <Field label="Description" span={12} htmlFor="ss-desc">
                <Input id="ss-desc" {...form.register("description")} />
              </Field>
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete shape / size?"
        description={deleting ? `${deleting.size_label} will be removed permanently.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
